import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import VSNTypography from "../../shared/VSNTypography";
import { PhysicalOnePagerResultStyle } from "../assessmentsView/physicalAssessment/PhysicalOnePagerResultStyle";

export const CPETGaugeCharts = ({ data }) => {
  if (!data) return null;

  const minValue = data.yellow_section.start;
  const maxValue = data.dark_green_section.end;
  const gaugeValue = parseFloat(data?.value);

  const totalRange = maxValue - minValue;
  const colorSections = [
    [
      (data.yellow_section.end - minValue) / totalRange,
      data.yellow_section.color,
    ],
    [
      (data.green_section.end - minValue) / totalRange,
      data.green_section.color,
    ],
    [1, data.dark_green_section.color],
  ];

  const chartOptions = {
    series: [
      {
        type: "gauge",
        startAngle: 225,
        endAngle: -45,
        min: minValue,
        max: maxValue,
        splitNumber: 10,
        axisLine: {
          lineStyle: {
            width: 10,
            color: colorSections,
          },
        },
        pointer: {
          width: 5,
          length: "60%",
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          distance: -2,
          fontSize: 16,
          formatter: (val) => {
            if (val === minValue) return `${minValue.toFixed(1)}`;
            if (val === maxValue) return `${maxValue.toFixed(1)}`;
            return "";
          },
        },
        detail: {
          fontSize: 20,
          fontWeight: "700",
          formatter: "{value}",
          offsetCenter: [0, "80%"],
        },
        data: [{ value: gaugeValue }],
      },
    ],
  };

  return (
    <Stack
      width={{ sm: "100%", md: "calc(33.33% - 16px)" }}
      alignItems="center"
    >
      <VSNTypography variant="h6" fontWeight={700} textAlign="center">
        {data?.title ?? ""}
      </VSNTypography>
      <ReactECharts option={chartOptions} style={{ width: 230, height: 230 }} />
      <VSNTypography
        backgroundColor="#FCEBD8"
        color="#E87637"
        padding="8px 16px"
        fontWeight={700}
        fontSize={18}
        mb="16px"
      >
        {data?.percentage ?? 0}%
      </VSNTypography>
      <VSNTypography textAlign={{ lg: "start", xs: "center" }}>
        {data?.description ?? ""}
      </VSNTypography>
    </Stack>
  );
};
export const LungHealthMeasurementsTable = ({
  tableData,
  headers,
  pdf,
  title,
}) => {
  return (
    <Grid container spacing={4} pt={2}>
      <Grid item xl={12} lg={12} xs={12}>
        <VSNTypography
          mb={{ sm: 0, xs: 2 }}
          sx={{ fontSize: pdf ? "16px" : "24px", fontWeight: 700 }}
        >
          {title ?? ""}
        </VSNTypography>
        {tableData.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{ backgroundColor: "var(--white)" }}
          >
            <Table
              sx={{
                borderCollapse: "collapse",
                minHeight: !pdf ? "550px" : "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  {headers?.map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        backgroundColor: "#EAEDF0",
                        border: "1px solid #CDD0E1",
                        textAlign: "center",
                        fontWeight: 700,
                        padding: pdf ? "8px" : "16px",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData?.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell
                      sx={{
                        border: "1px solid #CDD0E1",
                        textAlign: "center",
                        padding: "8px",
                      }}
                    >
                      {row.label}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "1px solid #CDD0E1",
                        textAlign: "center",
                        padding: "8px",
                      }}
                    >
                      {row.predicted}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "1px solid #CDD0E1",
                        textAlign: "center",
                        padding: "8px",
                      }}
                    >
                      {row.patient}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "1px solid #CDD0E1",
                        textAlign: "center",
                        padding: "8px",
                      }}
                    >
                      {row["%pred"]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <VSNTypography>No data available</VSNTypography>
        )}
      </Grid>
    </Grid>
  );
};

export const MetricChart = ({
  metric,
  currentValue,
  pastValue,
  unit,
  percentage,
  width = "100%",
}) => {
  const difference = currentValue - pastValue;
  const differenceColor = difference >= 0 ? "green" : "red";
  const classes = PhysicalOnePagerResultStyle();

  const option = {
    xAxis: {
      type: "category",
      data: [""],
      axisLine: {
        lineStyle: {
          color: "#ccc",
        },
      },
      axisTick: { show: false },
      axisLabel: { show: false },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
    },
    series: [
      {
        name: "Past",
        type: "bar",
        data: [pastValue],
        itemStyle: { color: "#809dff" },
        barWidth: "20%",
      },
      {
        name: "Current",
        type: "bar",
        data: [currentValue],
        itemStyle: { color: "#3b4a9f" },
        barWidth: "20%",
      },
    ],
    grid: { left: "20%", right: "20%", top: "2%", bottom: "2%" },
  };

  return (
    <Box textAlign="center" className={classes.barChart} width="180px">
      <ReactECharts option={option} style={{ height: "100px", width }} />
      <VSNTypography variant="h5">
        {currentValue} {unit ?? ""}
      </VSNTypography>
      <VSNTypography variant="body2">{metric}</VSNTypography>
      <VSNTypography variant="body2">
        Past {pastValue} |{" "}
        <span style={{ color: differenceColor }}>
          {percentage > 0 ? `+${percentage ?? 0}%` : `${percentage ?? 0}%`}
        </span>
      </VSNTypography>
    </Box>
  );
};

export const MetricTable = ({ rows }) => (
  <TableContainer
    component={Paper}
    sx={{
      borderRadius: 1,
      height: "fit-content",
      marginBottom: { lg: 0, xs: 2 },
    }}
  >
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: "#CDD0E1" }}>
          {rows?.header?.map((row, index) => (
            <TableCell
              sx={{
                padding: "12px",
                fontSize: "16px",
                fontWeight: 700,
              }}
              key={index}
              align="center"
            >
              {row}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows?.data?.map((row, index) => (
          <TableRow key={index}>
            <TableCell
              sx={{
                padding: "12px",
                fontSize: "16px",
                fontWeight: 700,
              }}
              align="center"
            >
              {row.label ?? "-"}
            </TableCell>
            <TableCell
              sx={{
                padding: "12px",
                fontSize: "16px",
                fontWeight: 700,
              }}
              align="center"
            >
              {row.past ?? "-"}
            </TableCell>
            <TableCell
              sx={{
                padding: "12px",
                fontSize: "16px",
                fontWeight: 700,
              }}
              align="center"
            >
              {row.current ?? "-"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export const DynamicVO2Chart = ({ chartData, height, width }) => {
  if (!chartData || Object?.keys(chartData)?.length === 0) {
    return <p>No data available</p>;
  }

  const xAxisData = chartData["x-axis"] || {};
  const y1AxisData = chartData["y1-axis"] || [];
  const y2AxisData = chartData["y2-axis"] || [];

  const getMarkAreas = (areaData) => {
    if (Array.isArray(areaData)) {
      return areaData?.map((area) => [
        {
          yAxis: area?.lower_area ?? area?.lower_area,
          xAxis: xAxisData?.min || undefined,
          yAxisIndex: 0,
          itemStyle: { color: areaData?.color },
        },
        {
          yAxis: area?.upper_area ?? area?.upper_area,
          xAxis: area?.max || undefined,
          yAxisIndex: 0,
        },
      ]);
    }

    if (areaData) {
      return [
        [
          {
            yAxis: areaData?.lower_area ?? areaData?.lower_area,
            xAxis: xAxisData?.min || undefined,
            yAxisIndex: 0,
            itemStyle: { color: areaData?.color },
          },
          {
            yAxis: areaData?.upper_area ?? areaData?.upper_area,
            xAxis: areaData?.max || undefined,
            yAxisIndex: 0,
          },
        ],
      ];
    }

    return [];
  };

  const markAreasY1 = getMarkAreas(chartData?.y1_shaded_area || []);

  const markAreasY2 = getMarkAreas(chartData?.y2_shaded_area || []);

  const y1Series = y1AxisData?.map((axis, index) => ({
    name: axis?.title || `Y1-${index}`,
    type: axis?.type || "line",
    yAxisIndex: 0,
    data: (xAxisData.value || [])?.map((x, i) => [x, axis?.value?.[i] || 0]),
    smooth: axis?.type !== "scatter",
    showSymbol: axis?.type === "scatter",
    symbolSize: axis?.type === "scatter" ? 6 : 0,
    itemStyle: { color: axis?.color || "blue" },
    lineStyle: axis?.type !== "scatter" ? { width: 2 } : undefined,
    ...(index === 0 && {
      markLine: {
        symbol: "none",
        lineStyle: { type: "dashed", color: "gray", width: 2 },
        label: {
          position: "insideEndTop",
          fontSize: 12,
          formatter: (param) => param.name,
        },
        data: [
          ...(chartData?.rer_1
            ? [{ xAxis: chartData?.rer_1, name: "RER 1" }]
            : []),
          ...(chartData?.vt_1
            ? [{ xAxis: chartData?.vt_1, name: "VT 1" }]
            : []),
          ...(chartData?.vt_2
            ? [{ xAxis: chartData?.vt_2, name: "VT 2" }]
            : []),
          ...(chartData?.max ? [{ xAxis: chartData?.max, name: "MAX" }] : []),
        ],
      },
      markArea: {
        silent: true,
        itemStyle: { opacity: 0.3 },
        data: markAreasY1,
      },
    }),
  }));

  const y2Series =
    y2AxisData.length > 0
      ? [
          {
            name: y2AxisData[0]?.title || "Y2",
            type: y2AxisData[0]?.type || "line",
            yAxisIndex: 1,
            data: (xAxisData.value || [])?.map((x, i) => [
              x,
              y2AxisData[0]?.value?.[i] || 0,
            ]),
            smooth: true,
            showSymbol: false,
            itemStyle: { color: y2AxisData[0]?.color || "green" },
            lineStyle: { width: 2 },
            markLine: {
              symbol: "none",
              lineStyle: { type: "dashed", color: "orange", width: 1 },
              label: {
                position: "insideEndTop",
                fontSize: 12,
                formatter: (param) => param.name,
              },
              data: chartData?.fev_1
                ? [{ yAxis: chartData?.fev_1, name: "FEV 1" }]
                : [],
            },
            markArea: {
              silent: true,
              itemStyle: { opacity: 0.3 },
              data: markAreasY2,
            },
          },
        ]
      : [];

  const option = {
    // title: {
    //   text: chartData?.title || "VO2 Chart",
    //   left: "center",
    // },
    tooltip: {
      trigger: "axis",
    },
    legend: {
      data: [
        ...y1AxisData?.map((axis) => axis.title),
        ...(y2AxisData.length ? [y2AxisData[0]?.title] : []),
      ].filter(Boolean),
      top: 20,
    },
    grid: {
      left: "10%",
      right: "10%",
      bottom: "10%",
    },
    xAxis: {
      type: "value",
      name: xAxisData?.title || "X-Axis",
      min: xAxisData?.min ?? undefined,
      max: xAxisData?.max ?? undefined,
      position: "middle",
      nameLocation: "middle",
      nameGap: 25,
      markArea: {
        data: markAreasY2,
      },
    },
    yAxis: [
      {
        type: "value",
        name: y1AxisData?.map((axis) => axis.title).join(" / ") || "Y1 Axis",

        min: y1AxisData.some((axis) => axis.min !== undefined)
          ? Math.min(
              ...y1AxisData?.map((axis) => axis.min ?? Number.POSITIVE_INFINITY)
            )
          : undefined,
        max: y1AxisData.some((axis) => axis.max !== undefined)
          ? Math.max(
              ...y1AxisData?.map((axis) => axis.max ?? Number.NEGATIVE_INFINITY)
            )
          : undefined,
        interval: y1AxisData.some((axis) => axis.diff !== undefined)
          ? Math.max(...y1AxisData?.map((axis) => axis.diff ?? 0))
          : undefined,
        position: "left",
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 30,
      },
      ...(y2AxisData.length
        ? [
            {
              type: "value",
              name: y2AxisData[0]?.title || "Y2 Axis",
              min: y2AxisData[0]?.min ?? undefined,
              max: y2AxisData[0]?.max ?? undefined,
              interval: y2AxisData[0]?.diff ?? undefined,
              position: "right",
              nameLocation: "middle",
              nameRotate: -90,
              nameGap: 30,
              splitLine: { show: false },
            },
          ]
        : []),
    ],
    series: [
      ...y1Series,
      ...y2Series,
      {
        type: "custom",
        renderItem: () => null,
        data: [],

        ...(chartData?.x_shaded_area
          ? {
              markArea: {
                silent: true,
                itemStyle: {
                  color: chartData?.x_shaded_area?.color || "rgba(0,0,0,0.1)",
                  opacity: 0.3,
                },
                data: [
                  [
                    { xAxis: chartData?.x_shaded_area?.min },
                    {
                      xAxis: chartData?.x_shaded_area?.max,
                      label: {
                        show: true,
                        position: "inside",
                        formatter: chartData?.x_shaded_area?.label,
                        rotate: 90,
                        color: "#000",
                        fontSize: 12,
                        fontWeight: "bold",
                      },
                    },
                  ],
                ],
              },
            }
          : {}),
      },
    ],
  };

  return <ReactECharts option={option} style={{ height, width }} />;
};

export const EnergyUseChart = ({ data, height = "100%", width = "100%" }) => {
  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
      },
      formatter: (params) => {
        let result = `${params[0]?.axisValueLabel} <br/>`;
        params.forEach((item) => {
          result +=
            `<span style="display:inline-block;margin-right:5px;
          border-radius:10px;width:9px;height:9px;
          background-color:${item.color};"></span>` +
            `${item.seriesName}: ${item.data[1] ?? "-"}<br/>`;
        });
        return result;
      },
    },

    xAxis: {
      type: "value",
      name: data["x-axis"]?.title,
      nameGap: 5,
      min: Math.min(...data["x-axis"]?.value),
      max: data["x-axis"]?.max ?? 1000,
      axisLine: { show: true },
      splitLine: { show: false },
      axisLabel: { show: false },
      axisTick: { show: false },
      position: "left",
      nameLocation: "middle",
    },
    yAxis: [
      {
        type: "value",
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        axisTick: { show: false },
        position: "left",
        offset: 10,
      },
      {
        type: "value",

        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        axisTick: { show: false },
      },
      {
        type: "value",
        min: data["y3-axis"][0]?.min,

        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: true },
        axisTick: { show: false },
        position: "left",
      },
      {
        type: "value",

        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: true },
        axisTick: { show: false },
      },
    ],
    grid: { top: 50, bottom: 30, left: 30, right: 30 },
    series: [
      {
        name: "Fat",
        type: "line",
        yAxisIndex: 0,
        data: data["x-axis"]?.value.map((x, i) => [
          x,
          data["y1-axis"][0]?.value[i],
        ]),
        lineStyle: { color: "#acc452", width: 3 },
        itemStyle: { color: "#acc452" },
        symbol: "none",
        smooth: 0.1,
        sampling: "average",
        connectNulls: true,
      },
      {
        name: "Carbs",
        type: "line",
        yAxisIndex: 1,
        data: data["x-axis"]?.value.map((x, i) => [
          x,
          data["y2-axis"][0]?.value[i],
        ]),
        lineStyle: { color: "#d86eeb", width: 3 },
        itemStyle: { color: "#d86eeb" },
        symbol: "none",
        smooth: 0.1,
        sampling: "average",
        connectNulls: true,
      },
      {
        name: "HR",
        type: "line",
        yAxisIndex: 2,
        data: data["x-axis"]?.value.map((x, i) => [
          x,
          data["y3-axis"][0]?.value[i],
        ]),
        lineStyle: { color: "#c40a0a", width: 3 },
        itemStyle: { color: "#c40a0a" },
        symbol: "none",
        smooth: 0.1,
        sampling: "average",
        connectNulls: true,
      },
      {
        name: "Work",
        type: "line",
        yAxisIndex: 3,
        data: data["x-axis"]?.value.map((x, i) => [
          x,
          data["y4-axis"][0]?.value[i],
        ]),
        lineStyle: { color: "#000", width: 3 },
        itemStyle: { color: "#000" },
        symbol: "none",
        smooth: 0.1,
        sampling: "average",
        connectNulls: true,
      },
      {
        name: "Zones",
        type: "line",
        data: [],
        markArea: {
          silent: true,
          itemStyle: { opacity: 0.9 },
          data: [
            [
              {
                xAxis: data?.gray_section?.start,
                itemStyle: { color: data?.gray_section?.color },
              },
              { xAxis: data?.gray_section?.end },
            ],
            [
              {
                xAxis: data?.blue_section?.start,
                itemStyle: { color: data?.blue_section?.color },
              },
              { xAxis: data?.blue_section?.end },
            ],
            [
              {
                xAxis: data?.green_section?.start,
                itemStyle: { color: data?.green_section?.color },
              },
              { xAxis: data?.green_section?.end },
            ],
            [
              {
                xAxis: data?.yellow_section?.start,
                itemStyle: { color: data?.yellow_section?.color },
              },
              { xAxis: data?.yellow_section?.end },
            ],
            [
              {
                xAxis: data?.maroon_section?.start,
                itemStyle: { color: data?.maroon_section?.color },
              },
              { xAxis: data?.maroon_section?.end },
            ],
          ],
        },
      },
    ],
  };
  return <ReactECharts option={option} style={{ height, width }} />;
};

export const YearOnYearOptionChart = ({
  data,
  height = "100%",
  width = "100%",
}) => {
  const current = data?.y_axis_current ?? [];
  const past = data?.y_axis_past ?? [];

  const yearOnYearOption = {
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: data?.x_axis?.value,
      axisLabel: { show: false },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: "#CBCBCF" } },
    },
    yAxis: [
      {
        type: "value",
        name: current[0]?.title,
        position: "left",
        min: current[0]?.min,
        max: Math.max(current[0]?.max ?? 0, past[0]?.max ?? 0),
        axisLine: { show: true },
        axisLabel: { show: true },
        axisTick: { show: false },
        splitLine: { show: false },
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 25,
      },
      {
        type: "value",
        name: current[1]?.title,
        position: "right",
        min: current[1]?.min,
        max: Math.max(current[1]?.max ?? 0, past[1]?.max ?? 0),
        axisLine: { show: true },
        axisLabel: { show: true },
        axisTick: { show: true },
        splitLine: { show: true },
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 30,
      },
    ],
    grid: { top: 30, bottom: 30, left: 60, right: 60 },
    series: [
      {
        name: `${current[0]?.title}(Current)`,
        type: "line",
        smooth: true,
        data: current[0]?.value,
        yAxisIndex: 0,
        symbol: "none",
        lineStyle: {
          type: current[0]?.type,
          color: current[0]?.color,
        },
        itemStyle: {
          color: current[0]?.color,
        },
      },
      {
        name: `${current[1]?.title}(Current)`,
        type: "line",
        smooth: true,
        data: current[1]?.value,
        yAxisIndex: 1,
        symbol: "none",
        lineStyle: {
          type: current[1]?.type,
          color: current[1]?.color,
        },
        itemStyle: {
          color: current[1]?.color,
        },
      },
      {
        name: `${past[0]?.title}(Past)`,
        type: "line",
        smooth: true,
        data: past[0]?.value,
        yAxisIndex: 0,
        symbol: "none",
        lineStyle: {
          type: past[0]?.type,
          color: past[0]?.color,
          opacity: 0.7,
        },
        itemStyle: {
          color: past[0]?.color,
        },
      },
      {
        name: `${past[1]?.title}(Past)`,
        type: "line",
        smooth: true,
        data: past[1]?.value,
        yAxisIndex: 1,
        symbol: "none",
        lineStyle: {
          type: past[1]?.type,
          color: past[1]?.color,
          opacity: 0.7,
        },
        itemStyle: {
          color: past[1]?.color,
        },
      },
    ],
  };

  return <ReactECharts option={yearOnYearOption} style={{ height, width }} />;
};

export const FlowChart = ({ chartData, height, width }) => {
  if (!chartData || !chartData["x-axis"] || !chartData["y1-axis"]) {
    return <p>No data available</p>;
  }

  const inspirationData = chartData["x-axis"][0]?.value ?? [];
  const expirationData = chartData["x-axis"][1]?.value ?? [];
  const inspirationFlow = chartData["y1-axis"][0]?.value ?? [];
  const expirationFlow = chartData["y1-axis"][1]?.value ?? [];

  if (
    !inspirationData.length ||
    !expirationData.length ||
    !inspirationFlow.length ||
    !expirationFlow.length
  ) {
    return <p>No data available</p>;
  }

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        let text = `${params[0]?.axisValue}<br/>`;
        params.forEach((item) => {
          text += `<span style="display:inline-block;width:10px;height:10px;margin-right:5px;background:${item.color};border-radius:50%;"></span>${item.seriesName}: ${item.data}<br/>`;
        });
        return text;
      },
    },
    legend: {
      data: [
        chartData["x-axis"][0]?.title ?? "Inspiration",
        chartData["x-axis"][1]?.title ?? "Expiration",
      ],
      top: "5%",
    },
    grid: {
      left: "10%",
      right: "10%",
      bottom: "10%",
    },
    xAxis: [
      {
        type: "category",
        data: inspirationData,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true },
        axisLabel: { show: false },
        min: chartData["x-axis"][0]?.min ?? 0,
        max: chartData["x-axis"][0]?.max ?? 200,
      },
      {
        type: "category",
        data: expirationData,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: "Flow (L/sec)",
        position: "left",
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 25,
        min: chartData["y1-axis"][0]?.min ?? -10,
        max: chartData["y1-axis"][1]?.max ?? 10,
        interval: chartData["y1-axis"][0]?.diff ?? 2,
      },
    ],
    series: [
      {
        name: chartData["x-axis"][0]?.title ?? "Inspiration",
        type: "line",
        data: inspirationFlow,
        color: chartData["x-axis"][0]?.color ?? "#0088CC",
        xAxisIndex: 0,
      },
      {
        name: chartData["x-axis"][1]?.title ?? "Expiration",
        type: "line",
        data: expirationFlow,
        color: chartData["x-axis"][1]?.color ?? "#FF5733",
        xAxisIndex: 1,
      },
    ],
  };

  return <ReactECharts option={option} style={{ height, width }} />;
};

export const CPETSecondChart = ({ chartData, height, width = "100%" }) => {
  const safeGet = (obj, path, defaultValue = null) => {
    return path.reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : defaultValue),
      obj
    );
  };

  const xValues = safeGet(chartData, ["x-axis", "value"], []);
  const xMin = safeGet(chartData, ["x-axis", "min"], 0);
  const xMax = safeGet(chartData, ["x-axis", "max"], 1000);

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const axisLabel = params[0]?.axisValueLabel;
        const seenValues = new Set();
        let tooltipText = `${axisLabel}<br/>`;

        params.forEach((item) => {
          const value = item.value[1];
          const key = `${value}`;
          if (!seenValues.has(key)) {
            seenValues.add(key);
            tooltipText += `
          <span style="display:inline-block;width:10px;height:10px;margin-right:5px;background:${item.color}"></span>
          ${item.seriesName}: ${value}<br/>
        `;
          }
        });

        return tooltipText;
      },
    },
    legend: {
      data: [
        safeGet(chartData, ["y1-axis", 0, "title"], "Y1 Axis 1"),
        safeGet(chartData, ["y2-axis", 0, "title"], "Y2 Axis"),
        safeGet(chartData, ["y1-axis", 1, "title"], "Y1 Axis 2"),
      ],
    },
    xAxis: {
      type: "value",
      name: safeGet(chartData, ["x-axis", "title"], "X Axis"),
      nameGap: 25,
      min: xMin,
      max: xMax,
      axisLine: { show: true },
      splitLine: { show: false },
      axisLabel: { show: true },
      axisTick: { show: false },
      position: "left",
      nameLocation: "middle",
    },
    yAxis: [
      {
        type: "value",
        min: safeGet(chartData, ["y1-axis", 0, "min"], undefined),
        max: safeGet(chartData, ["y1-axis", 0, "max"], undefined),
        // interval: safeGet(chartData, ["y1-axis", 0, "diff"], 10),
        axisLine: { show: false },
        splitLine: { show: true },
        axisLabel: { show: true },
        axisTick: { show: false },
        position: "left",
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 30,
        name: safeGet(chartData, ["y1-axis", 0, "title"], 0),
      },
      {
        type: "value",
        min: safeGet(chartData, ["y2-axis", 0, "min"], undefined),
        max: safeGet(chartData, ["y2-axis", 0, "max"], undefined),
        // interval: safeGet(chartData, ["y2-axis", 0, "diff"], 10),
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: true },
        axisTick: { show: false },
        position: "right",
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: 25,
        name: safeGet(chartData, ["y2-axis", 0, "title"], 0),
      },
      {
        type: "value",
        min: safeGet(chartData, ["y1-axis", 1, "min"], 0),
        max: safeGet(chartData, ["y1-axis", 1, "max"], 100),
        // interval: safeGet(chartData, ["y1-axis", 1, "diff"], 10),
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        axisTick: { show: false },
      },
    ],
    grid: {
      left: "10%",
      right: "10%",
      bottom: "10%",
    },
    series: [
      {
        name: safeGet(chartData, ["y1-axis", 0, "title"], "Y1 Axis 1"),
        type: safeGet(chartData, ["y1-axis", 0, "type"], "line"),
        color: safeGet(chartData, ["y1-axis", 0, "color"], "#000"),
        yAxisIndex: 0,
        data: xValues.map((x, i) => [
          x,
          safeGet(chartData, ["y1-axis", 0, "value", i], 0),
        ]),
        symbolSize: 6,
      },
      {
        name: safeGet(chartData, ["y2-axis", 0, "title"], "Y2 Axis"),
        type: safeGet(chartData, ["y2-axis", 0, "type"], "line"),
        color: safeGet(chartData, ["y2-axis", 0, "color"], "#000"),
        yAxisIndex: 1,
        data: xValues.map((x, i) => [
          x,
          safeGet(chartData, ["y2-axis", 0, "value", i], 0),
        ]),
        symbolSize: 6,
      },
      {
        name: safeGet(chartData, ["y1-axis", 1, "title"], "Y1 Axis 2"),
        type: safeGet(chartData, ["y1-axis", 1, "type"], "line"),
        color: safeGet(chartData, ["y1-axis", 1, "color"], "#000"),
        yAxisIndex: 2,
        data: xValues.map((x, i) => [
          x,
          safeGet(chartData, ["y1-axis", 1, "value", i], 0),
        ]),
        symbolSize: 6,
      },
    ],
  };

  return <ReactECharts option={option} style={{ height, width }} />;
};
